import React from "react";
import { format } from "date-fns";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useFayUploaderNameQuery } from "#graphql/supergraphHooks";
import { fileIconType } from "#utils/classificationGrouping";
import { toGlobalId } from "#utils/globalId";
import type { PackageDocument } from "#viewModelTypes";

export type RemovePackageDocumentConfirmModalProps = {
  isOpen: boolean;
  document: PackageDocument | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
};

/** Open findings currently attached to the document — the count the reviewer
 *  weighs before removing (those findings get withdrawn with the binding). */
export function openFindingCount(document: PackageDocument): number {
  return document.sections
    .flatMap((section) => section.checks)
    .filter((check) => check.findingId !== null && check.isOpen).length;
}

/** One labelled metadata row in the confirm summary. */
const MetadataRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Flex gap={8}>
    <Text variant="secondary-md" asSpan>
      {label}
    </Text>
    <Text variant="regular-md" asSpan>
      {value}
    </Text>
  </Flex>
);

/**
 * RemovePackageDocumentConfirmModal — danger-confirm for removing a bound
 * document from its package.
 *
 * The metadata block is an acceptance criterion, not styling: Fay has no
 * Investor representation, so sibling PCAPs in the same package are
 * distinguishable only by filename, uploader, upload date, and finding count.
 * The modal must show all four so the reviewer doesn't remove the wrong
 * sibling. Uploader-name resolution is lazy (only while the modal is open) and
 * degrades to the raw user id if the lookup fails.
 *
 * No undo at MVP — removal is a terminal supersession; re-upload is the
 * recovery path, and the copy says so.
 */
export const RemovePackageDocumentConfirmModal: React.FC<
  RemovePackageDocumentConfirmModalProps
> = ({ isOpen, document, onClose, onConfirm, loading }) => {
  const uploaderUserId = document?.uploadedByUserId ?? null;
  // fay stores the numeric user id; the staffUser read takes the relay global
  // id. Number(null) is 0 and Number("garbage") is NaN — both would encode a
  // bogus global id — so anything non-finite skips the lookup entirely (no id is
  // computed) and the display falls back to the raw id / em dash.
  const numericUploaderId = uploaderUserId === null ? NaN : Number(uploaderUserId);
  const canResolveUploader = Number.isFinite(numericUploaderId);
  const { data: uploaderData } = useFayUploaderNameQuery(
    isOpen && canResolveUploader
      ? { variables: { userId: toGlobalId("UserObjectType", numericUploaderId) } }
      : { skip: true },
  );

  if (!document) {
    return null;
  }

  const uploaderName =
    uploaderData?.staffUser?.fullName ?? (uploaderUserId ? `User ${uploaderUserId}` : "—");
  const findingCount = openFindingCount(document);

  return (
    // While the removal write is in flight the modal is unclosable: Escape,
    // click-outside, and the close button all call onHide, which would let the
    // user dismiss mid-removal and start a *different* row's removal before the
    // first settles (the hook also guards re-entrancy). The Cancel/Remove
    // buttons stay disabled via `loading` below.
    <ModalV2
      show={isOpen}
      onHide={onClose}
      size="small"
      title="Remove document"
      closeOnClickOutside={!loading}
      escapeToClose={!loading}
      closeButton={!loading}
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={12}>
          <Flex alignItems="center" gap={8}>
            <Icon type={fileIconType(document.filename)} variant="solid" aria-hidden="true" />
            <Text asSpan lines={1} variant="strong-md">
              {document.filename}
            </Text>
          </Flex>
          <Flex flexDirection="column" gap={4}>
            <MetadataRow label="Uploaded by" value={uploaderName} />
            <MetadataRow
              label="Uploaded"
              value={
                document.uploadedAt
                  ? format(new Date(document.uploadedAt), "MMM d, yyyy h:mm a")
                  : "—"
              }
            />
            <MetadataRow label="Open findings" value={findingCount} />
          </Flex>
          <Text variant="regular-md">
            This will remove the document from this package and withdraw its checks and
            findings. This cannot be undone — upload a new document to replace it.
          </Text>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={8}>
          <Button variant="neutral" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={loading}>
            Remove
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
